import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useModule } from '../modules/use-modules.js';
import {
  useCreateEvlRun,
  useEvlEmployeeSearch,
  type EvlEmployeeMatch,
  type EvlIntakeBody,
} from './use-evl-intake.js';

const PURPOSES = [
  { value: 'landlord', label: 'Landlord / rental' },
  { value: 'bank', label: 'Bank / loan' },
  { value: 'visa', label: 'Visa / immigration' },
  { value: 'reference', label: 'Employment reference' },
  { value: 'other', label: 'Other' },
] as const;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function EvlIntakePage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { data: moduleDetail, isLoading, error } = useModule(companyId, 'evl');

  if (!companyId) {
    return <p className="text-red-600">Missing company context.</p>;
  }
  if (isLoading) return <p className="text-slate-500">Loading…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load the EVL module.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }
  if (!moduleDetail) return <p className="text-slate-500">Module not found.</p>;

  const blocker = (() => {
    if (!moduleDetail.enabled) return 'The EVL module is disabled for this company.';
    const unmetRequired = moduleDetail.roleBindings.filter(
      (b) => !b.optional && b.connectionStatus !== 'connected',
    );
    if (unmetRequired.length > 0) {
      const roles = unmetRequired.map((b) => `${b.role} (${b.provider})`).join(', ');
      return `Required integrations not connected: ${roles}.`;
    }
    return null;
  })();

  if (blocker) {
    return (
      <div className="max-w-2xl space-y-4">
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="font-medium text-amber-900">EVL is not ready to accept runs.</p>
          <p className="mt-1 text-sm text-amber-800">{blocker}</p>
          <Link
            to={`/companies/${companyId}/modules/evl`}
            className="mt-3 inline-block text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            Configure in Modules →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <EvlIntakeForm
      companyId={companyId}
      onCreated={(runId) => navigate(`/companies/${companyId}/modules/evl/runs/${runId}`)}
    />
  );
}

function EvlIntakeForm({
  companyId,
  onCreated,
}: {
  companyId: string;
  onCreated: (runId: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 250);
  const [selected, setSelected] = useState<EvlEmployeeMatch | null>(null);
  const [purpose, setPurpose] = useState<string>(PURPOSES[0].value);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientOrg, setRecipientOrg] = useState('');
  const [note, setNote] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: searchData, isFetching: searching } = useEvlEmployeeSearch(
    companyId,
    debouncedQuery,
    { enabled: debouncedQuery.length > 0 },
  );

  const createRun = useCreateEvlRun(companyId);

  const canSubmit = useMemo(
    () => !!selected && recipientName.trim() !== '' && /.+@.+\..+/.test(recipientEmail),
    [selected, recipientName, recipientEmail],
  );

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!selected || !canSubmit) return;
    setSubmitError(null);
    const body: EvlIntakeBody = {
      employeeId: selected.id,
      recipient: {
        name: recipientName.trim(),
        email: recipientEmail.trim(),
        ...(recipientOrg.trim() ? { organisation: recipientOrg.trim() } : {}),
      },
      purpose,
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    try {
      const res = await createRun.mutateAsync(body);
      onCreated(res.runId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit');
    }
  }

  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      className="max-w-2xl space-y-6"
    >
      <div>
        <h1 className="text-2xl font-semibold">New Employment Verification Letter</h1>
        <p className="mt-1 text-sm text-slate-600">
          Select the employee, add the recipient, and submit. The letter will be generated, signed,
          archived, and emailed automatically.
        </p>
      </div>

      <section className="space-y-2">
        <label className="block text-sm font-medium text-slate-700">Employee</label>
        {selected ? (
          <div className="flex items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2">
            <div>
              <p className="font-medium text-slate-900">{selected.displayName}</p>
              <p className="text-xs text-slate-500">
                {selected.jobTitle ?? '—'} · {selected.workEmail ?? 'no email'}
              </p>
            </div>
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-slate-700"
              onClick={() => {
                setSelected(null);
                setQuery('');
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Start typing a name, email, or job title…"
              className="w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              autoComplete="off"
            />
            {debouncedQuery.length > 0 && (
              <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {searching && <p className="px-3 py-2 text-sm text-slate-500">Searching…</p>}
                {!searching && (searchData?.items ?? []).length === 0 && (
                  <p className="px-3 py-2 text-sm text-slate-500">No matches.</p>
                )}
                {(searchData?.items ?? []).map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      setSelected(e);
                      setQuery('');
                    }}
                    className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{e.displayName}</p>
                      <p className="text-xs text-slate-500">
                        {e.jobTitle ?? '—'} · {e.workEmail ?? 'no email'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="recipient-name" className="block text-sm font-medium text-slate-700">
            Recipient name
          </label>
          <input
            id="recipient-name"
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            required
          />
        </div>
        <div>
          <label htmlFor="recipient-email" className="block text-sm font-medium text-slate-700">
            Recipient email
          </label>
          <input
            id="recipient-email"
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            required
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="recipient-org" className="block text-sm font-medium text-slate-700">
            Organisation (optional)
          </label>
          <input
            id="recipient-org"
            type="text"
            value={recipientOrg}
            onChange={(e) => setRecipientOrg(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </section>

      <section>
        <label htmlFor="purpose" className="block text-sm font-medium text-slate-700">
          Purpose
        </label>
        <select
          id="purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {PURPOSES.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </section>

      <section>
        <label htmlFor="note" className="block text-sm font-medium text-slate-700">
          Note (optional)
        </label>
        <textarea
          id="note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </section>

      {submitError && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Link
          to={`/companies/${companyId}/modules/evl`}
          className="text-sm text-slate-600 hover:text-slate-800"
        >
          ← Back to module
        </Link>
        <button
          type="submit"
          disabled={!canSubmit || createRun.isPending}
          className={clsx(
            'rounded-md px-5 py-2 font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500',
            canSubmit && !createRun.isPending
              ? 'bg-brand-600 hover:bg-brand-700'
              : 'cursor-not-allowed bg-slate-300',
          )}
        >
          {createRun.isPending ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </form>
  );
}
